import { Icon } from '@iconify/react';
import { Divider, LoadingOverlay, MultiSelect } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { Dispatch } from 'react';
import { generateStatusMapping } from '../../../api/integrations-client/integrations-client';
import { Button } from '../../../components/button/button';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { newCOLORS } from '../../../styles/colors';
import type { JDCReducerAction, JDCState } from '../jdc/jdc.type';
import { bloomfilterStatusOptions } from './views.data';
import styles from './views.module.css';

type StatusesProps = {
  state: JDCState;
  dispatch: Dispatch<JDCReducerAction>;
};

export function StatusView({ state, dispatch }: StatusesProps) {
  const { bloomfilterStatuses, options } = state;
  const organization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);

  const externalStatuses = options?.statuses || [];

  const mappedStatuses = Object.values(bloomfilterStatuses || {})
    .map((status) => status)
    .flat();

  const generateStatusMappingMutation = useMutation({
    mutationKey: ['generateStatusMapping', externalStatuses] as const,
    mutationFn: async () => {
      const data = await generateStatusMapping(organization?.id || '', externalStatuses);
      const bloomfilterStatuses = Object.entries(data || {}).reduce(
        (map: { [key: string]: string[] }, [statusLabel, mappedStatuses]) => {
          const bloomfilterStatus = bloomfilterStatusOptions.find((s) => s.label === statusLabel);
          if (bloomfilterStatus) {
            map[bloomfilterStatus.name] = mappedStatuses;
          }
          return map;
        },
        {},
      );
      dispatch({ type: 'bloomfilterStatuses', payload: bloomfilterStatuses });
      return data;
    },
  });

  return (
    <div id={styles.viewContainer}>
      <LoadingOverlay visible={!state.options?.statuses?.length} />
      <h4>Status Mapping</h4>
      <div className={styles.flexRowSpaceBetween}>
        <p>Help us understand your Jira statuses</p>
        {portfolio?.organization.allow_ai_use ? (
          <div className={styles.flexColumn}>
            <Button
              loading={generateStatusMappingMutation.isPending}
              color={generateStatusMappingMutation.isError ? newCOLORS.red : newCOLORS.indigo}
              styles={{ root: { width: '200px' } }}
              onClick={() => generateStatusMappingMutation.mutate()}
              size="lg"
            >
              {generateStatusMappingMutation.isPending ? 'Generating...' : 'Auto Map'}
            </Button>
            {generateStatusMappingMutation.isError ? (
              <>
                <p className={styles.autoMappingError}>service currently unavailable</p>
                <p className={styles.autoMappingError}>try again later</p>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      <div style={{ display: 'flex' }}>
        <div className={`${styles.statusListContainer} ${styles.statusBloomfilter}`}>
          <b>Bloomfilter</b>
          {bloomfilterStatusOptions.map((bloomfilterStatus) => (
            <div className={styles.multiselectContainer} key={bloomfilterStatus.name}>
              <b>{bloomfilterStatus.label}</b>
              <div className={styles.dropdownContainer}>
                <b className={styles.equalsContainer}>=</b>
                <MultiSelect
                  value={bloomfilterStatuses?.[bloomfilterStatus.name]}
                  onChange={(value: string[]) =>
                    dispatch({ type: 'bloomfilterStatuses', payload: { [bloomfilterStatus.name]: value } })
                  }
                  data={externalStatuses}
                  styles={{ root: { width: '300px' } }}
                />
              </div>
            </div>
          ))}
        </div>
        <Divider orientation="vertical" />
        <div className={`${styles.statusListContainer} ${styles.statusExternal}`}>
          <b>Jira</b>
          {externalStatuses.map((jiraStatus) => (
            <div key={jiraStatus} className={styles.externalStatusRow}>
              <div className={styles.dropdownContainer}>
                <b>{jiraStatus}</b>
                {mappedStatuses.includes(jiraStatus) ? (
                  <Icon icon="material-symbols:check-small-rounded" color={newCOLORS.tangerine} width="48" />
                ) : null}
              </div>
              <Divider size={1} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
